import React, { useState, useEffect } from 'react';
import { LikeOutlined, LikeFilled, DislikeOutlined, DislikeFilled, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { useTheme } from 'styled-components';
import { submitSessionFeedback } from '@nxsys/services/api/ai';
import { FeedbackWrapper, FeedbackCommentInput } from './index.styled';

interface SessionFeedbackProps {
  sessionId: string;
}

const SessionFeedback: React.FC<SessionFeedbackProps> = ({
  sessionId,
}) => {
  const theme = useTheme() as any;
  const [rating, setRating] = useState<'up' | 'down' | undefined>();
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Slide-in on mount
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => setDismissed(true), 300);
  };

  // Auto-dismiss after showing "Thanks" for 3 seconds
  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(dismiss, 3000);
    return () => clearTimeout(timer);
  }, [submitted]);

  // Reset when session changes
  useEffect(() => {
    setRating(undefined);
    setComment('');
    setShowComment(false);
    setSubmitted(false);
    setDismissed(false);
    setVisible(false);
    setTimeout(() => setVisible(true), 50);
  }, [sessionId]);

  const handleRating = async (newRating: 'up' | 'down') => {
    if (submitting) return;
    setRating(newRating);
    setSubmitting(true);
    try {
      await submitSessionFeedback(sessionId, { rating: newRating });
      setShowComment(true);
    } catch {
      message.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!rating || !comment.trim()) return;
    setSubmitting(true);
    try {
      await submitSessionFeedback(sessionId, { rating, comment: comment.trim() });

      setShowComment(false);
      setSubmitted(true);
    } catch {
      message.error('Failed to save comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (dismissed) return null;

  const slideStyle: React.CSSProperties = {
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
  };

  if (submitted && !showComment) {
    return (
      <FeedbackWrapper style={slideStyle}>
        <CheckOutlined style={{ color: theme.palette.success.main, fontSize: 12 }} />
        <span>Thanks for your feedback!</span>
        <button
          onClick={() => setSubmitted(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: theme.palette.text.secondary, fontSize: 11 }}
        >
          Edit
        </button>
      </FeedbackWrapper>
    );
  }

  return (
    <div style={slideStyle}>
      <FeedbackWrapper>
        <span>Was this helpful?</span>
        <button
          onClick={() => handleRating('up')}
          disabled={submitting}
          title="Thumbs up"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: rating === 'up' ? theme.palette.success.main : theme.palette.text.secondary, fontSize: 14 }}
          aria-label="Thumbs up"
        >
          {rating === 'up' ? <LikeFilled /> : <LikeOutlined />}
        </button>
        <button
          onClick={() => handleRating('down')}
          disabled={submitting}
          title="Thumbs down"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: rating === 'down' ? theme.palette.error.main : theme.palette.text.secondary, fontSize: 14 }}
          aria-label="Thumbs down"
        >
          {rating === 'down' ? <DislikeFilled /> : <DislikeOutlined />}
        </button>
        <button
          onClick={dismiss}
          title="Dismiss"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: theme.palette.text.disabled, fontSize: 10, marginLeft: 'auto' }}
          aria-label="Dismiss feedback"
        >
          <CloseOutlined />
        </button>
      </FeedbackWrapper>
      {showComment && (
        <div style={{ padding: '0 16px 8px', display: 'flex', gap: 8, alignItems: 'center', height: 32 }}>
          <FeedbackCommentInput
            placeholder="Optional comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmitComment();
              }
            }}
          />
          <button
            onClick={handleSubmitComment}
            disabled={submitting || !comment.trim()}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: submitting || !comment.trim() ? theme.palette.text.disabled : theme.palette.primary.main, fontSize: 12, whiteSpace: 'nowrap', fontWeight: 500 }}
          >
            Submit
          </button>
          <button
            onClick={() => { setShowComment(false); setSubmitted(true); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: theme.palette.text.secondary, fontSize: 12, whiteSpace: 'nowrap' }}
          >
            Skip
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionFeedback;
